import { createContext, useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { useAuth } from "../context/authcontext";
import { collection, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../components/firebase';
import { UpsertUsuarioPostventa } from '../components/dbfirebase';
import { buildFullName, resolveNameParts } from '../utils/nameUtils';

export const GeneralContext = createContext(null);

export const useGeneral = () => useContext(GeneralContext);

const formatDateToYmd = (dateValue) => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const timestampToDateInputValue = (value) => {
  if (value && typeof value.toDate === 'function') {
    return formatDateToYmd(value.toDate());
  }
  if (value instanceof Date) {
    return formatDateToYmd(value);
  }
  return '';
};

const normalizeProfileProducts = (profile) => {
  const products = Array.isArray(profile?.Productos) ? profile.Productos : [];
  return {
    ...profile,
    Productos: products.map((product) => ({
      ...product,
      FechaCompra: timestampToDateInputValue(product?.FechaCompra),
    })),
  };
};

export function GeneralProvider({ children }) {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const buildDefaultProfile = useCallback(() => {
    const authNameParts = resolveNameParts({ displayName: user?.displayName });
    return {
      uid: user?.uid || '',
      Nombre: authNameParts.nombre,
      Apellido: authNameParts.apellido,
      Titular: buildFullName({
        nombre: authNameParts.nombre,
        apellido: authNameParts.apellido,
        fallback: user?.displayName || '',
      }),
      Email: user?.email || '',
      Telefono: '',
      CuitDni: '',
      Direccion: '',
      Localidad: '',
      Provincia: '',
      CodigoPostal: '',
    };
  }, [user?.uid, user?.displayName, user?.email]);

  const loadUserProfile = useCallback(async () => {
    if (!user?.uid) {
      setProfileData(null);
      return null;
    }

    setProfileLoading(true);
    try {
      const defaults = buildDefaultProfile();
      const profileRef = doc(db, 'PerfilesUsuarios', user.uid);
      const postventaRef = doc(db, 'UsuariosPostventa', user.uid);
      const [snapshot, postventaSnap] = await Promise.all([getDoc(profileRef), getDoc(postventaRef)]);

      if (snapshot.exists() || postventaSnap.exists()) {
        const mergedRaw = {
          ...defaults,
          ...(postventaSnap.exists() ? postventaSnap.data() : {}),
          ...(snapshot.exists() ? snapshot.data() : {}),
        };
        const nameParts = resolveNameParts({
          nombre: mergedRaw.Nombre,
          apellido: mergedRaw.Apellido,
          titular: mergedRaw.Titular,
          displayName: user?.displayName,
        });
        const merged = {
          ...mergedRaw,
          Nombre: nameParts.nombre,
          Apellido: nameParts.apellido,
          Titular: buildFullName({
            nombre: nameParts.nombre,
            apellido: nameParts.apellido,
            fallback: mergedRaw.Titular || user?.displayName || '',
          }),
          Email: user?.email || snapshot.data()?.Email || postventaSnap.data()?.Email || '',
          uid: user.uid,
        };
        const normalizedMerged = normalizeProfileProducts(merged);
        setProfileData(normalizedMerged);
        return normalizedMerged;
      }

      const normalizedDefaults = normalizeProfileProducts(defaults);
      setProfileData(normalizedDefaults);
      return normalizedDefaults;
    } catch (error) {
      console.error('Error al cargar perfil:', error);
      const fallback = buildDefaultProfile();
      const normalizedFallback = normalizeProfileProducts(fallback);
      setProfileData(normalizedFallback);
      return normalizedFallback;
    } finally {
      setProfileLoading(false);
    }
  }, [user?.uid, user?.email, user?.displayName, buildDefaultProfile]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const upsertUserProfile = useCallback(async (partialProfile = {}) => {
    if (!user?.uid) throw new Error('Usuario no autenticado');
    const fixedEmail = user?.email || '';
    const nameParts = resolveNameParts({
      nombre: partialProfile.Nombre,
      apellido: partialProfile.Apellido,
      titular: partialProfile.Titular,
      displayName: user?.displayName,
    });
    const fullName = buildFullName({
      nombre: nameParts.nombre,
      apellido: nameParts.apellido,
      fallback: partialProfile.Titular || user?.displayName || '',
    });

    const normalized = {
      Nombre: nameParts.nombre,
      Apellido: nameParts.apellido,
      Titular: fullName,
      Email: fixedEmail,
      Telefono: partialProfile.Telefono ?? '',
      CuitDni: partialProfile.CuitDni ?? '',
      Direccion: partialProfile.Direccion ?? '',
      Localidad: partialProfile.Localidad ?? '',
      Provincia: partialProfile.Provincia ?? '',
      CodigoPostal: partialProfile.CodigoPostal ?? '',
      uid: user.uid,
      updatedAt: serverTimestamp(),
    };

    const profileRef = doc(db, 'PerfilesUsuarios', user.uid);
    await Promise.all([
      setDoc(profileRef, normalized, { merge: true }),
      UpsertUsuarioPostventa({ uid: user.uid, profile: normalized }),
    ]);

    const nextProfile = {
      ...buildDefaultProfile(),
      ...(profileData || {}),
      ...normalized,
      uid: user.uid,
    };
    const normalizedProfile = normalizeProfileProducts(nextProfile);
    setProfileData(normalizedProfile);
    return normalizedProfile;
  }, [user?.uid, user?.email, user?.displayName, profileData, buildDefaultProfile]);

  const generateSecretKey = useCallback((docName) => {
    if (!user?.uid) return '0';
    const docRef = doc(collection(db, docName));
    const id = docRef.id;
    const number = id.substring(5, 10).split("")
      .map((char) => char.charCodeAt())
      .join('');
    return number;
  }, [user?.uid]);

  const value = useMemo(() => ({
    user,
    generateSecretKey,
    profileData,
    userData: profileData,
    profileLoading,
    refreshUserProfile: loadUserProfile,
    upsertUserProfile,
  }), [user, generateSecretKey, profileData, profileLoading, loadUserProfile, upsertUserProfile]);

  return (
    <GeneralContext.Provider value={value}>
      {children}
    </GeneralContext.Provider>
  );
}
